import { useState } from 'react';
import { LastPage } from '@mui/icons-material';
import { Box, IconButton, Tab, Tabs, Typography } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { makeStyles } from '@mui/styles';
import { format } from 'date-fns';
import {
  EstateWorkItem,
  EstateWorkItem_RequestTemplateAnswerInput,
  EstateWorkItemStatus,
  EstateWorkItemSubStatus,
  useEstate_SendActionNeededTasksReminderMutation,
  useEstateInputDataInstance_GetEstateInputDataInstancesByEstateWorkItemIdQuery,
  useEstateWorkItem_UpdateEstateWorkItemMutation,
} from 'queries';

import { Button } from 'common/button';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import EstateWorkItemDocumentsForSignatureList from './estate-work-item-documents-for-signature-list';
import EstateWorkItemEstateDetails from './estate-work-item-estate-details';
import EstateWorkItemGenerateDocument from './estate-work-item-generate-document';
import EstateWorkItemInternalNotes from './estate-work-item-internal-notes';
import EstateWorkItemPaymentDetails from './estate-work-item-payment-details';
import EstateWorkItemPublishedNotes from './estate-work-item-published-notes';
import EstateWorkItemRequestDocumentForSignature from './estate-work-item-request-document-for-signature';
import EstateWorkItemStatusSelect from './estate-work-item-status-select';
import EstateWorkItemSubStatusSelect from './estate-work-item-sub-status-select';
import EstateWorkItemVaultDetails from './estate-work-item-vault-details';
import RequestInfoDocModal from './request-info-doc-modal';
import TaskActivityNotes from './task-activity-notes';

const useStyles = makeStyles(() => ({
  container: {
    padding: '20px',
    color: '#1C2541',
  },
  loadingContainer: {
    padding: '20px',
    color: '#1C2541',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  header: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
  },
  subtitle: {
    fontSize: '16px',
    fontWeight: 700,
  },
  tabsContainer: {
    borderBottom: '1px solid #ECEEF1',
    marginBottom: '8px',
  },
  lastPageIcon: {
    color: '#1C2541',
  },
  column: {
    display: 'flex',
    width: '100%',
    padding: '20px 0',
    borderBottom: '1px solid #ECEEF1',
    alignItems: 'center',
  },
  columnHeader: {
    width: '200px',
  },
  headerText: {
    display: 'flex',
    alignItems: 'center',
  },
  columnBody: {
    display: 'flex',
    flex: 1,
  },
  stackedDataFirst: {
    display: 'block',
    lineHeight: '1.43',
    width: '100%',
  },
  button: {
    padding: '10px',
  },
  documentsForSignatureCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    width: '100%',
  },
  requestTemplateBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
  },
}));

type EstateWorkItemDrawerProps = {
  estateWorkItem: EstateWorkItem;
  onClose: () => void;
  refetchEstateWorkItems: () => void;
  handleImpersonateView: (type: string, estateWorkItem: EstateWorkItem) => void;
  setCurrentEstateWorkItem: React.Dispatch<React.SetStateAction<EstateWorkItem | null>>;
};

enum EstateWorkItemDrawerTabs {
  ESTATE_WORK_ITEM_INFORMATION = 0,
  INTERNAL_NOTES = 1,
  PUBLISHED_NOTES = 2,
}

const EstateWorkItemDrawer = ({
  estateWorkItem,
  onClose,
  refetchEstateWorkItems,
  handleImpersonateView,
  setCurrentEstateWorkItem,
}: EstateWorkItemDrawerProps) => {
  const classes = useStyles();
  const [currentTab, setCurrentTab] = useState(
    EstateWorkItemDrawerTabs.ESTATE_WORK_ITEM_INFORMATION,
  );
  const [activeRequestInfoDocEstateWorkItem, setActiveRequestInfoDocEstateWorkItem] =
    useState<EstateWorkItem>();

  const { data: estateInputDataInstancesData, loading: estateInputDataInstancesLoading } =
    useEstateInputDataInstance_GetEstateInputDataInstancesByEstateWorkItemIdQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: {
        input: {
          estateWorkItemId: estateWorkItem.id,
          portalId: estateWorkItem.estate!.portalId,
        },
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting estate input data instances by estate work item ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const estateInputDataInstances = estateInputDataInstancesData?.data || [];

  const [updateEstateWorkItem, { loading: updateEstateWorkItemExecuting }] =
    useEstateWorkItem_UpdateEstateWorkItemMutation({
      onCompleted: () => refetchEstateWorkItems(),
      onError: (e) => {
        showAlert({
          message: 'Error updating estate work item: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [sendReminder, { loading: sendReminderExecuting }] =
    useEstate_SendActionNeededTasksReminderMutation({
      variables: {
        portalId: estateWorkItem.estate!.portalId,
      },
      onCompleted: () => {
        showToast({
          message: 'Success. Action Needed Tasks Reminder Sent',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'Error sending action needed tasks reminder: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const onUpdateEstateWorkItemStatus = async (status: EstateWorkItemStatus): Promise<void> => {
    const subStatus =
      status === EstateWorkItemStatus.Completed
        ? EstateWorkItemSubStatus.Completed
        : estateWorkItem.subStatus;

    await updateEstateWorkItem({
      variables: {
        input: {
          id: estateWorkItem.id,
          status,
          subStatus,
        },
      },
    });

    setCurrentEstateWorkItem({ ...estateWorkItem, status, subStatus });

    showToast({
      message: 'Status updated successfully!',
      severity: NotificationSeverity.SUCCESS,
    });
  };

  const onUpdateEstateWorkItemSubStatus = async (
    subStatus: EstateWorkItemSubStatus,
  ): Promise<void> => {
    const status =
      subStatus === EstateWorkItemSubStatus.Completed ||
      subStatus === EstateWorkItemSubStatus.Closed
        ? EstateWorkItemStatus.Completed
        : estateWorkItem.status;

    await updateEstateWorkItem({
      variables: {
        input: {
          id: estateWorkItem.id,
          status,
          subStatus,
        },
      },
    });

    setCurrentEstateWorkItem({ ...estateWorkItem, status, subStatus });

    showToast({
      message: 'Sub-Status updated successfully!',
      severity: NotificationSeverity.SUCCESS,
    });
  };

  const renderContent = () => {
    return (
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography className={classes.title}>{estateWorkItem.estate!.estateName}</Typography>
          <IconButton onClick={onClose}>
            <LastPage className={classes.lastPageIcon} />
          </IconButton>
        </div>
        <Typography className={classes.subtitle}>{estateWorkItem.workText}</Typography>
        <Box className={classes.tabsContainer}>
          <Tabs
            value={currentTab}
            onChange={(_: React.SyntheticEvent, newValue: number) => setCurrentTab(newValue)}
            textColor="inherit"
          >
            <Tab label="Task Information" iconPosition="end" />
            <Tab label="Internal Notes" />
            <Tab label="Published Notes" />
          </Tabs>
        </Box>
        {currentTab === EstateWorkItemDrawerTabs.INTERNAL_NOTES && (
          <EstateWorkItemInternalNotes key={estateWorkItem.id} estateWorkItem={estateWorkItem} />
        )}
        {currentTab === EstateWorkItemDrawerTabs.PUBLISHED_NOTES && (
          <EstateWorkItemPublishedNotes key={estateWorkItem.id} estateWorkItem={estateWorkItem} />
        )}
        {currentTab === EstateWorkItemDrawerTabs.ESTATE_WORK_ITEM_INFORMATION && (
          <div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Portal</Typography>
              </div>
              <div className={classes.columnBody}>{estateWorkItem.estate?.portal?.name}</div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Estate</Typography>
              </div>
              <div className={classes.columnBody}>
                <EstateWorkItemEstateDetails
                  estateWorkItem={estateWorkItem}
                  estateInputDataInstances={estateInputDataInstances}
                  handleImpersonateView={handleImpersonateView}
                />
              </div>
            </div>

            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Task Activity</Typography>
              </div>
              <div className={classes.columnBody}>
                <TaskActivityNotes estateWorkItem={estateWorkItem} />
              </div>
            </div>

            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Status</Typography>
              </div>
              <div className={classes.columnBody}>
                {updateEstateWorkItemExecuting && <Spinner />}
                {!updateEstateWorkItemExecuting && (
                  <EstateWorkItemStatusSelect
                    estateWorkItem={estateWorkItem}
                    onUpdateEstateWorkItemStatus={onUpdateEstateWorkItemStatus}
                  />
                )}
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Task Status</Typography>
              </div>
              <div className={classes.columnBody}>
                {updateEstateWorkItemExecuting && <Spinner />}
                {!updateEstateWorkItemExecuting && (
                  <EstateWorkItemSubStatusSelect
                    estateWorkItem={estateWorkItem}
                    onUpdateEstateWorkItemSubStatus={onUpdateEstateWorkItemSubStatus}
                  />
                )}
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Action Needed Tasks Reminder</Typography>
              </div>
              <div className={classes.columnBody}>
                <div className={classes.stackedDataFirst}>
                  <div>
                    <strong>Last Action Needed Tasks Reminder Date:</strong>
                  </div>
                  {estateWorkItem.estate?.lastActionNeededTasksReminderDate ? (
                    <div className={classes.stackedDataFirst}>
                      <div>
                        <Typography variant="caption">
                          {format(
                            Date.parse(estateWorkItem.estate?.lastActionNeededTasksReminderDate),
                            'LLL d y',
                          )}
                        </Typography>
                      </div>
                      <div className={classes.stackedDataFirst}>
                        <Typography variant="caption">
                          {format(
                            Date.parse(estateWorkItem.estate?.lastActionNeededTasksReminderDate),
                            'h:mm aaa',
                          )}
                        </Typography>
                      </div>
                    </div>
                  ) : (
                    'N/A'
                  )}
                  <div>
                    {estateWorkItem.status !== EstateWorkItemStatus.Completed && (
                      <div className={classes.stackedDataFirst}>
                        <Button
                          disabled={sendReminderExecuting}
                          onClick={() => sendReminder()}
                          size="small"
                          color="info"
                          className={classes.button}
                        >
                          Send Action Needed Tasks Reminder
                        </Button>
                        <Typography fontSize="small" color="red">
                          This action will send one reminder email for all estates in this portal if
                          they have any tasks in 'Action Needed'. No email will be sent if there are
                          no action needed tasks, however the date will still be updated.
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Vault</Typography>
              </div>
              <div className={classes.columnBody}>
                <EstateWorkItemVaultDetails
                  estate={estateWorkItem.estate}
                  handleImpersonateView={(type: string) =>
                    handleImpersonateView(type, estateWorkItem)
                  }
                />
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Notes last updated</Typography>
              </div>
              <div className={classes.columnBody}>
                {estateWorkItem.estateWorkItemNotesLastUpdatedByUserDate ? (
                  <div className={classes.stackedDataFirst}>
                    <div>
                      <Typography variant="caption">
                        {format(
                          Date.parse(estateWorkItem.estateWorkItemNotesLastUpdatedByUserDate),
                          'LLL d y',
                        )}
                      </Typography>
                    </div>
                    <div className={classes.stackedDataFirst}>
                      <Typography variant="caption">
                        {format(
                          Date.parse(estateWorkItem.estateWorkItemNotesLastUpdatedByUserDate),
                          'h:mm aaa',
                        )}
                      </Typography>
                    </div>
                  </div>
                ) : (
                  'N/A'
                )}
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>Generate Document</div>
              <div className={classes.columnBody}>
                <EstateWorkItemGenerateDocument
                  estateWorkItem={estateWorkItem}
                  estateInputDataInstances={estateInputDataInstances}
                  refetchEstateWorkItems={refetchEstateWorkItems}
                />
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>Request Info/Docs</div>
              <div className={classes.columnBody}>
                <div className={classes.stackedDataFirst}>
                  <Button
                    onClick={() => setActiveRequestInfoDocEstateWorkItem(estateWorkItem)}
                    size="small"
                    color="info"
                    className={classes.button}
                  >
                    Request Info/Docs
                  </Button>
                  <Typography fontSize="small">
                    Use this to request custom info/docs from the estate.
                  </Typography>
                </div>
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>Upload Requested Docs for Signature</div>
              <div className={classes.columnBody}>
                <EstateWorkItemRequestDocumentForSignature
                  estateWorkItemId={estateWorkItem.id}
                  portalId={estateWorkItem.estate!.portalId}
                  folderId={estateWorkItem.estate!.documentsFolderId!}
                  refetchEstateWorkItems={refetchEstateWorkItems}
                />
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnBody}>
                <div className={classes.documentsForSignatureCell}>
                  <EstateWorkItemDocumentsForSignatureList
                    estateWorkItem={estateWorkItem}
                    portalId={estateWorkItem.estate!.portalId}
                  />
                </div>
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Payment Details</Typography>
              </div>
              <div className={classes.columnBody}>
                {estateWorkItem.estatePayment ? (
                  <EstateWorkItemPaymentDetails estatePayment={estateWorkItem.estatePayment} />
                ) : (
                  'N/A'
                )}
              </div>
            </div>
            {estateWorkItem.requestTemplateAnswersJson && (
              <div className={classes.column}>
                <div className={classes.columnHeader}>
                  <Typography className={classes.headerText}>Requested Template</Typography>
                </div>
                <div className={classes.columnBody}>
                  <div className={classes.requestTemplateBody}>
                    {JSON.parse(estateWorkItem.requestTemplateAnswersJson).map(
                      (answer: EstateWorkItem_RequestTemplateAnswerInput) => (
                        <div>
                          <strong>{answer.question}</strong>
                          <div>{answer.answer}</div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={onClose}
      PaperProps={{
        sx: { width: '744px' },
      }}
    >
      {estateInputDataInstancesLoading ? (
        <div className={classes.loadingContainer}>
          <Spinner />
        </div>
      ) : (
        renderContent()
      )}
      {activeRequestInfoDocEstateWorkItem && (
        <RequestInfoDocModal
          estateWorkItem={activeRequestInfoDocEstateWorkItem}
          onClose={() => setActiveRequestInfoDocEstateWorkItem(undefined)}
        />
      )}
    </Drawer>
  );
};

export default EstateWorkItemDrawer;
